import { useState, useRef, useCallback } from "react";
import { X, GripVertical } from "lucide-react";
import { FIELD_TYPES } from "./fieldTypes";

export default function PlacedField({ field, signers, onMove, onResize, onDelete, containerRef }) {
  const fieldDef = FIELD_TYPES.find((f) => f.type === field.type) || FIELD_TYPES[0];
  const signer = signers[field.recipientIndex] || signers[0];
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const startRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    if (e.target.dataset.resize) return;
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    startRef.current = { x: e.clientX - field.x, y: e.clientY - field.y };

    const onMouseMove = (ev) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      let nx = ev.clientX - startRef.current.x;
      let ny = ev.clientY - startRef.current.y;
      nx = Math.max(0, Math.min(nx, rect.width - field.width));
      ny = Math.max(0, Math.min(ny, rect.height - field.height));
      onMove(field.id, nx, ny);
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [field, onMove, containerRef]);

  const handleResizeDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    startRef.current = { x: e.clientX, y: e.clientY, w: field.width, h: field.height };

    const onMouseMove = (ev) => {
      const dw = ev.clientX - startRef.current.x;
      const dh = ev.clientY - startRef.current.y;
      const nw = Math.max(40, startRef.current.w + dw);
      const nh = Math.max(20, startRef.current.h + dh);
      onResize(field.id, nw, nh);
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [field, onResize]);

  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: "absolute",
        left: field.x,
        top: field.y,
        width: field.width,
        height: field.height,
        border: `2px solid ${fieldDef.color}`,
        borderRadius: 4,
        background: `${fieldDef.color}18`,
        cursor: dragging ? "grabbing" : "grab",
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "0 6px",
        boxSizing: "border-box",
        zIndex: dragging || resizing ? 100 : 10,
        userSelect: "none",
      }}
    >
      <GripVertical size={12} color={fieldDef.color} style={{ flexShrink: 0, opacity: 0.6 }} />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{
          fontSize: 10,
          fontWeight: 600,
          color: fieldDef.color,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {fieldDef.label}
        </div>
        <div style={{
          fontSize: 9,
          color: "#888",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {signer?.label || "Signer"}
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
        style={{
          position: "absolute", top: -8, right: -8,
          width: 16, height: 16, borderRadius: "50%",
          background: "#ef4444", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 0,
        }}
      >
        <X size={10} color="#fff" />
      </button>

      <div
        data-resize="true"
        onMouseDown={handleResizeDown}
        style={{
          position: "absolute", bottom: -4, right: -4,
          width: 10, height: 10,
          background: fieldDef.color, borderRadius: 2,
          cursor: "nwse-resize",
        }}
      />
    </div>
  );
}
